import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, X } from 'lucide-react';
import { alertsService } from '../../../services';
import type { AlertRule as AlertRuleAPI, CreateAlertRuleData, UpdateAlertRuleData } from '../../../services/alerts.service';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import StatusBadge from '../../../components/common/StatusBadge';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuthStore } from '../../../stores/authStore';
import { USER_TYPES } from '../../../utils/constants';

interface AlertsSettingsTabProps {
  settings?: any;
  onUpdate?: (settings: any) => void;
}

interface CreateRuleForm {
  alert: string;
  expr: string;
  duration: string;
  severity: string;
  groupName: string;
  summary: string;
  description: string;
}

const defaultForm: CreateRuleForm = {
  alert: '',
  expr: '',
  duration: '5m',
  severity: 'warning',
  groupName: 'custom',
  summary: '',
  description: '',
};

const AlertsSettingsTab = ({ }: AlertsSettingsTabProps) => {
  const toast = useToast();
  const { canCreate: rawCanCreate, canUpdate: rawCanUpdate, canDelete: rawCanDelete } = usePermissions();
  const { user } = useAuthStore();
  // Alert rules are GLOBAL Prometheus rules (cross-tenant), while a ChannelAdmin's
  // authority is a subset of tenants — editing them would reach outside that
  // scope. Present Alerts as read-only for a ChannelAdmin so no write control is
  // shown (the backend also gates these writes). usePermissions() returns
  // all-true for type 5, so gate explicitly here.
  const isChannelAdmin = user?.type === USER_TYPES.CHANNEL_ADMIN;
  const canCreate = rawCanCreate && !isChannelAdmin;
  const canUpdate = rawCanUpdate && !isChannelAdmin;
  const canDelete = rawCanDelete && !isChannelAdmin;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState<AlertRuleAPI[]>([]);
  const [readOnly, setReadOnly] = useState(false);
  const [readOnlyMessage, setReadOnlyMessage] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<CreateRuleForm>(defaultForm);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; ruleId: string | null }>({
    isOpen: false,
    ruleId: null
  });

  useEffect(() => {
    fetchAlertRules();
  }, []);

  const fetchAlertRules = async () => {
    try {
      setLoading(true);
      const data = await alertsService.getAlertRules();
      setRules(data.rules);
      setReadOnly(data.readOnly);
      setReadOnlyMessage(data.readOnlyMessage);
    } catch (error) {
      console.error('Failed to fetch alert rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async () => {
    if (!createForm.alert || !createForm.expr) {
      toast.error('Validation Error', 'Alert name and expression are required');
      return;
    }

    try {
      setSaving(true);
      const ruleData: CreateAlertRuleData = {
        alert: createForm.alert,
        expr: createForm.expr,
        duration: createForm.duration,
        severity: createForm.severity,
        groupName: createForm.groupName,
        labels: {
          category: 'Custom',
        },
        annotations: {
          summary: createForm.summary || `Alert: ${createForm.alert}`,
          description: createForm.description || 'Custom alert rule',
        },
      };

      await alertsService.createAlertRule(ruleData);
      await fetchAlertRules(); // Refresh list
      setShowCreateForm(false);
      setCreateForm(defaultForm);
      toast.success('Rule Created', 'Alert rule created successfully');
    } catch (error: any) {
      toast.error('Creation Failed', `Failed to create alert rule: ${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleRule = async (ruleId: string, currentlyEnabled: boolean) => {
    try {
      const updateData: UpdateAlertRuleData = {
        enabled: !currentlyEnabled,
      };
      await alertsService.updateAlertRule(ruleId, updateData);
      await fetchAlertRules(); // Refresh list
      toast.success('Rule Updated', `Alert rule ${currentlyEnabled ? 'disabled' : 'enabled'} successfully`);
    } catch (error) {
      console.error('Failed to toggle alert rule:', error);
      toast.error('Update Failed', 'Failed to update alert rule');
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteConfirm.ruleId) return;

    try {
      await alertsService.deleteAlertRule(deleteConfirm.ruleId);
      await fetchAlertRules(); // Refresh list
      setDeleteConfirm({ isOpen: false, ruleId: null });
      toast.success('Rule Deleted', 'Alert rule deleted successfully');
    } catch (error) {
      console.error('Failed to delete alert rule:', error);
      toast.error('Delete Failed', 'Failed to delete alert rule');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700';
      case 'info':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Separate rules into system and custom
  const systemRules = rules.filter(r => r.managed);
  const customRules = rules.filter(r => !r.managed);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Read-only banner for Kubernetes deployments */}
      {readOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
          <strong>Custom alert rules are read-only in this deployment.</strong>
          <p className="mt-1 text-amber-800">
            {readOnlyMessage || 'Rules are managed via GitOps and cannot be edited through the UI.'}
          </p>
        </div>
      )}

      {/* Custom Alert Rules */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Custom Alert Rules</h2>
            <p className="text-sm text-gray-600">Create and manage your own custom alert rules</p>
          </div>
          {canCreate && !readOnly && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="btn-secondary text-sm inline-flex items-center space-x-2"
            >
              {showCreateForm ? (
                <>
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>Add Rule</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="p-4 bg-gray-50 rounded-lg mb-4 border-2 border-primary-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Create New Alert Rule</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Alert Name*</label>
                <input
                  type="text"
                  value={createForm.alert}
                  onChange={(e) => setCreateForm({ ...createForm, alert: e.target.value })}
                  placeholder="e.g., HighCPUUsage"
                  className="input-base w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Severity</label>
                <select
                  value={createForm.severity}
                  onChange={(e) => setCreateForm({ ...createForm, severity: e.target.value })}
                  className="input-base w-full text-sm"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">PromQL Expression*</label>
                <input
                  type="text"
                  value={createForm.expr}
                  onChange={(e) => setCreateForm({ ...createForm, expr: e.target.value })}
                  placeholder="e.g., edge_cpu_usage_percent > 80"
                  className="input-base w-full text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Duration</label>
                <input
                  type="text"
                  value={createForm.duration}
                  onChange={(e) => setCreateForm({ ...createForm, duration: e.target.value })}
                  placeholder="e.g., 5m"
                  className="input-base w-full text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">How long condition must be true (e.g., 5m, 1h)</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Group</label>
                <input
                  type="text"
                  value={createForm.groupName}
                  onChange={(e) => setCreateForm({ ...createForm, groupName: e.target.value })}
                  placeholder="custom"
                  className="input-base w-full text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Summary</label>
                <input
                  type="text"
                  value={createForm.summary}
                  onChange={(e) => setCreateForm({ ...createForm, summary: e.target.value })}
                  placeholder="e.g., High CPU usage detected on {{ $labels.edge_name }}"
                  className="input-base w-full text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  placeholder="Detailed description of the alert..."
                  rows={2}
                  className="input-base w-full text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateForm(defaultForm);
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRule}
                disabled={saving || !createForm.alert || !createForm.expr}
                className="btn-primary text-sm inline-flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Creating...' : 'Create Rule'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Custom Rules List */}
        <div className="space-y-3">
          {customRules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm">No custom alert rules yet. Click "Add Rule" to create one.</p>
            </div>
          ) : (
            customRules.map((rule) => (
              <div key={rule.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-sm font-semibold text-gray-900">{rule.alert}</h3>
                      <StatusBadge
                        status={rule.enabled ? 'Online' : 'Offline'}
                      />
                      <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(rule.severity)}`}>
                        {rule.severity}
                      </span>
                      {rule.editable && (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                          Editable
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p><strong>Expression:</strong> <code className="bg-gray-100 px-1 py-0.5 rounded">{rule.expr}</code></p>
                      <p><strong>Duration:</strong> {rule.duration}</p>
                      <p><strong>Group:</strong> {rule.group}</p>
                      {rule.annotations?.summary && (
                        <p><strong>Summary:</strong> {rule.annotations.summary}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {canUpdate && (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          onChange={() => handleToggleRule(rule.id, rule.enabled)}
                          className="sr-only peer"
                          disabled={!rule.editable}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                      </label>
                    )}
                    {canDelete && rule.editable && (
                      <button
                        onClick={() => setDeleteConfirm({ isOpen: true, ruleId: rule.id })}
                        className="p-1.5 hover:bg-red-50 rounded text-red-600 hover:text-red-700"
                        title="Delete Rule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* System Alert Rules (Read-Only) */}
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900">System Alert Rules</h2>
          <p className="text-sm text-gray-600">Pre-configured rules managed by the system (read-only)</p>
        </div>
        <div className="space-y-3">
          {systemRules.map((rule) => (
            <div key={rule.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-sm font-semibold text-gray-900">{rule.alert}</h3>
                    <StatusBadge
                      status={rule.enabled ? 'Online' : 'Offline'}
                    />
                    <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(rule.severity)}`}>
                      {rule.severity}
                    </span>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-700">
                      System
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p><strong>Expression:</strong> <code className="bg-gray-100 px-1 py-0.5 rounded">{rule.expr}</code></p>
                    <p><strong>Duration:</strong> {rule.duration}</p>
                    <p><strong>Group:</strong> {rule.group}</p>
                    <p><strong>File:</strong> {rule.file}</p>
                    {rule.annotations?.summary && (
                      <p><strong>Summary:</strong> {rule.annotations.summary}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, ruleId: null })}
        onConfirm={handleDeleteRule}
        title="Delete Alert Rule"
        message="Are you sure you want to delete this alert rule? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

export default AlertsSettingsTab;
